/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import { useContext, useRef, useState } from 'react';
import { head, isEmpty, findLast } from 'lodash';
import { css, StyleSheet } from 'aphrodite';
import {
  ActionButton,
  FilePickerInput,
  LoneIconButton,
  PopoverList,
  Upload,
  uploadSingleFile,
} from '@blend/enterprise-components';

import { addDocsToLending, deleteDocument } from 'blb-api/loans';
import { updateTitleOrder } from 'blb-api/title';
import { uploadFile } from 'blb-api/files';
import { BlendBridge } from 'blb-types/blendBridge';
import { openUrlInNewTab } from 'blb-utils/misc';

import { CurativeConfig, TitleContext } from './TitleTab';
import { CommitmentUploadStatus } from './types';

interface CommitmentUploadHeaderActionProps {
  loan: BlendBridge.API.Loan;
  order: Title.Orders.Order;
  curativeConfig: CurativeConfig;
}

const styles = StyleSheet.create({
  headerButtonWrapper: {
    display: 'flex',
  },
  viewDocButton: {
    display: 'inline-block',
    marginRight: 10,
  },
});

export const CommitmentUploadHeaderAction: React.FC<CommitmentUploadHeaderActionProps> = ({
  loan,
  order,
  curativeConfig,
}) => {
  const { setTitleOrder } = useContext(TitleContext);
  const fileUploadInput = useRef<HTMLInputElement>(null);
  const [status, setStatus] = useState<CommitmentUploadStatus>(
    order?.commitmentDocID ? CommitmentUploadStatus.COMPLETED : CommitmentUploadStatus.DEFUALT,
  );

  const handleUploadEvent = async (_fileId: string, _file: File, upload: Upload) => {
    if (upload.fileName && upload.url) {
      setStatus(CommitmentUploadStatus.UPLOADED);
      const { data: docs } = await addDocsToLending({
        files: [{ fileKey: upload.url, fileName: upload.fileName, type: curativeConfig.documentType }],
        loanId: loan.loanId,
      });

      /**
       * The server-side title order will be updated automatically via Webhook, so we just
       * need to update the frontend state here. See https://git.blendlabs.com/blend/title/pull/568
       */
      if (!isEmpty(docs)) {
        const doc = head(docs);
        const update = {
          commitmentDocID: doc!.documentId,
          status: 'RECEIVED' as Title.Orders.OrderStatusType.RECEIVED,
        };
        setTitleOrder({ ...order, ...update });
        setStatus(CommitmentUploadStatus.COMPLETED);
      }
    }
  };

  const handleUploadCommitment = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = head(e.target.files);
    if (file) {
      setStatus(CommitmentUploadStatus.UPLOADING);
      await uploadSingleFile({
        file,
        handleUploadEvent,
        otherUploadParams: { loanId: loan.loanId },
        uploadFile,
      });
    }
  };

  const handleViewCommitment = () => {
    if (order && order.commitmentDocID) {
      const enableSecondaryAuth = true;
      openUrlInNewTab(
        `/api/internal/loans/${loan.loanId}/documents/${order.commitmentDocID}?inline=true`,
        enableSecondaryAuth,
      );
    }
  };

  const handleRemoveCommitment = async () => {
    if (!order.commitmentDocID) return;

    const prevStatus = findLast(order.orderStatuses, (orderStatus: Title.Orders.OrderStatus) => {
      return orderStatus.type === 'ACCEPTED' || orderStatus.type === 'ORDERED';
    });

    const update = {
      commitmentDocID: null,
      status: prevStatus?.type || ('ORDERED' as Title.Orders.OrderStatusType.ORDERED),
    };
    await deleteDocument(loan.loanId, order.commitmentDocID);
    await updateTitleOrder(order.id, order.tenant, update);
    setTitleOrder({ ...order, ...update });
    setStatus(CommitmentUploadStatus.DEFUALT);
  };

  switch (status) {
    case CommitmentUploadStatus.COMPLETED:
      return (
        <div className={css(styles.headerButtonWrapper)}>
          <div className={css(styles.viewDocButton)}>
            <ActionButton
              btnSize="md"
              id="it-viewCommitment"
              label={curativeConfig.documentViewText}
              onClick={handleViewCommitment}
              theme="tertiary"
            />
          </div>
          <PopoverList
            btnSize="md"
            buttonConfigs={[
              {
                id: 'it-removeCommitment',
                label: 'Remove document',
                onClick: handleRemoveCommitment,
              },
            ]}
          >
            <LoneIconButton btnSize={36} icon="more_horiz" iconSize={22} id="it-moreActions" theme="tertiary" />
          </PopoverList>
        </div>
      );
    case CommitmentUploadStatus.UPLOADED:
      return <ActionButton btnSize="md" icon="check" label="Uploaded" theme="secondaryAffirm" />;
    case CommitmentUploadStatus.UPLOADING:
      return (
        <ActionButton btnSize="md" icon="loop" id="it-uploadCommitment" isLoading label="Uploading" theme="tertiary" />
      );
    default:
      return (
        <div className={css(styles.headerButtonWrapper)}>
          <FilePickerInput
            accept={'.png,.jpg,.jpeg,.tif,.tiff,.svg,.ico,.pdf'}
            id="DocsTab-documentsUploadInput"
            onChange={handleUploadCommitment}
            ref={fileUploadInput}
          >
            <ActionButton
              btnSize="md"
              disabled={false}
              id="it-uploadCommitment"
              label={curativeConfig.documentUploadText}
              onClick={() => fileUploadInput.current!.click()}
            />
          </FilePickerInput>
        </div>
      );
  }
};
